import { 
  EyeOutlined, 
  MailOutlined, 
  CalendarOutlined, 
  DollarOutlined,
  ShoppingOutlined,
  CopyOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  UserOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { Button, TableColumnsType, Tooltip, Tag, Modal, Table, message, Typography } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';

import { WrapperPage } from '@components/atoms';
import { GenericList } from '@components/molecules';
import { useOrders, Order, OrderItem } from '@hooks';

const { Title, Paragraph } = Typography;

// Status color mapping
const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    pending: 'orange',
    paid: 'green',
    processing: 'blue',
    shipped: 'cyan',
    delivered: 'green',
    cancelled: 'red',
  };
  return colors[status] || 'default';
};

// Styled section wrapper for modal
const OrderSection = ({ title, icon, children, noWrapper }: { title: string; icon?: React.ReactNode; children: React.ReactNode; noWrapper?: boolean }) => (
  <div className="mb-6">
    <div className="flex items-center gap-2 mb-4">
      {icon && <span className="text-yellow-principal text-lg">{icon}</span>}
      <Title level={5} className="font-android !font-normal !text-white !mb-0 uppercase tracking-wider">
        {title}
      </Title>
    </div>
    {noWrapper ? (
      children
    ) : (
      <div className="border-2 border-zinc-700 p-5 bg-zinc-900/50">
        {children}
      </div>
    )}
  </div>
);

export const OrdersManagementPage = () => {
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Fetch orders (enable fetching for this page)
  const { orders } = useOrders({ enableOrdersFetch: true });
  const ordersList = orders.data || [];
  const isLoading = orders.isLoading;

  // Open modal to view order details
  const openOrderDetails = (order: Order) => {
    setSelectedOrder(order);
    setDetailModalOpen(true);
  };

  // Format price
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  // Copy to clipboard
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard`);
  };

  // Table columns configuration for order items
  const itemColumns: TableColumnsType<OrderItem> = [
    {
      title: 'Product',
      dataIndex: 'productName',
      key: 'productName',
      render: (text: string) => (
        <span className="font-android text-white">{text}</span>
      ),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (qty: number) => (
        <span className="font-android text-white">{qty}</span>
      ),
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      width: 120,
      render: (price: number) => (
        <span className="font-android text-yellow-principal font-bold">
          {formatPrice(price)}
        </span>
      ),
    },
    {
      title: 'Subtotal',
      key: 'subtotal',
      width: 120,
      render: (_, record) => (
        <span className="font-android text-yellow-principal font-bold">
          {formatPrice(record.price * record.quantity)}
        </span>
      ),
    },
  ];

  // Table columns configuration for orders
  const columns: TableColumnsType<Order> = [
    {
      title: 'Order Number',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      render: (text: string) => (
        <span className="font-android text-yellow-principal font-semibold">{text}</span>
      ),
    },
    {
      title: 'Customer Email',
      dataIndex: 'customerEmail',
      key: 'customerEmail',
      render: (text: string) => (
        <span className="font-['Barlow'] text-white">{text}</span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)} className="font-android uppercase">
          {status}
        </Tag>
      ),
    },
    {
      title: 'Items',
      dataIndex: 'items',
      key: 'itemCount',
      width: 80,
      render: (items: OrderItem[]) => (
        <span className="font-['Barlow'] text-white">{items?.length || 0}</span>
      ),
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 150,
      render: (amount: number) => (
        <span className="font-android text-yellow-principal font-bold text-lg">
          {formatPrice(amount)}
        </span>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date: string) => (
        <span className="font-['Barlow'] text-white">
          {dayjs(date).format('MMM DD, YYYY HH:mm')}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Tooltip title="View details">
          <Button
            icon={<EyeOutlined style={{ color: '#E3AF25' }} />}
            type="text"
            size="large"
            onClick={(e) => {
              e.stopPropagation();
              openOrderDetails(record);
            }}
            className="order-action-button !bg-transparent hover:!bg-transparent !border-none hover:!border-none"
            style={{ color: '#E3AF25' }}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative w-full h-[400px]">
        <div className="careers-bg opacity-40 absolute inset-0 w-full h-full"></div>
        <div className="container relative h-full flex flex-col justify-center items-center mx-auto px-4 py-12 md:py-16 lg:py-20 z-10">
          <Title level={1} className="font-android !font-normal text-white text-center">
            ORDERS MANAGEMENT
          </Title>
          <div className="max-w-3xl mx-auto">
            <Paragraph className="font-barlow text-gray-300 text-center md:text-lg lg:!text-xl">
              View and manage all customer orders
            </Paragraph>
          </div>
        </div>
      </section>

      <section className="w-full py-14 bg-second-black-carbon">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-1 gap-4">
            <Title level={2} className="text-yellow-principal font-android !font-normal m-0">ORDERS</Title>
          </div>

          <Paragraph className="text-gray-300 font-barlow mb-8">
            View and manage all customer orders
          </Paragraph>

          <WrapperPage isLoading={isLoading}>
            <GenericList<Order>
              columns={columns}
              dataSource={ordersList}
              onRow={(record) => ({
                onClick: () => openOrderDetails(record),
                className: 'cursor-pointer hover:bg-zinc-800',
              })}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
              }}
            />
          </WrapperPage>

          {/* Modal for order details */}
          <Modal
            title={null}
            open={detailModalOpen}
            onCancel={() => setDetailModalOpen(false)}
            footer={null}
            width={900}
            closable={false}
            styles={{
              mask: { backdropFilter: 'blur(10px)', backgroundColor: 'rgba(0,0,0,0.8)' },
              content: { 
                backgroundColor: 'transparent', 
                boxShadow: 'none',
                padding: 0,
              },
            }}
            className="order-details-modal"
          >
            {selectedOrder && (
              <div className="bg-first-dark-gray border-2 border-zinc-600">
                {/* Header */}
                <div className="relative border-b border-zinc-700 px-8 py-6 flex items-center justify-between bg-second-black-carbon">
                  <div className="flex items-center gap-3">
                    <ShoppingOutlined className="text-yellow-principal text-2xl" />
                    <Title level={3} className="font-android !font-normal !text-white !mb-0 uppercase tracking-wider">
                      ORDER DETAILS
                    </Title>
                  </div>
                  <Button
                    type="text"
                    icon={<CloseOutlined className="text-xl !text-white" />}
                    onClick={() => setDetailModalOpen(false)}
                    className="!text-white hover:!text-yellow-principal hover:!bg-zinc-800 transition-colors !w-10 !h-10 flex items-center justify-center"
                    style={{ color: 'white' }}
                  />
                </div>

                {/* Modal Content */}
                <div className="px-8 py-6 max-h-[70vh] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                  {/* Order Number Section */}
                  <OrderSection title="Order Information" icon={<InfoCircleOutlined />}>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="font-android text-zinc-400 text-xs uppercase tracking-wider mb-2">
                          Order Number
                        </p>
                        <div className="flex items-center gap-2">
                          <span className="font-android text-yellow-principal text-2xl font-normal tracking-wide">
                            {selectedOrder.orderNumber}
                          </span>
                          <Tooltip title="Copy order number">
                            <Button
                              type="text"
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={() => copyToClipboard(selectedOrder.orderNumber, 'Order number')}
                              className="!text-yellow-principal hover:!text-yellow-500"
                            />
                          </Tooltip>
                        </div>
                      </div>
                      <div>
                        <p className="font-android text-zinc-400 text-xs uppercase tracking-wider mb-2">
                          Status
                        </p>
                        <Tag 
                          color={getStatusColor(selectedOrder.status)} 
                          className="font-android uppercase text-base px-4 py-1"
                        >
                          {selectedOrder.status}
                        </Tag>
                      </div>
                    </div>
                  </OrderSection>

                  {/* Customer Information Section */}
                  <OrderSection title="Customer Information" icon={<UserOutlined />}>
                    <div>
                      <p className="font-android text-zinc-400 text-xs uppercase tracking-wider mb-2">
                        Email Address
                      </p>
                      <div className="flex items-center gap-2">
                        <MailOutlined className="text-yellow-principal" />
                        <span className="font-barlow text-white text-base">
                          {selectedOrder.customerEmail}
                        </span>
                        <Tooltip title="Copy email">
                          <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => copyToClipboard(selectedOrder.customerEmail, 'Email')}
                            className="!text-yellow-principal hover:!text-yellow-500"
                          />
                        </Tooltip>
                      </div>
                    </div>
                  </OrderSection>

                  {/* Financial Information Section */}
                  <OrderSection title="Financial Information" icon={<DollarOutlined />}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-android text-zinc-400 text-xs uppercase tracking-wider mb-2">
                          Total Amount
                        </p>
                        <p className="font-android text-yellow-principal text-3xl">
                          {formatPrice(selectedOrder.totalAmount)}
                        </p>
                      </div>
                      <div className="bg-yellow-principal/10 p-4 rounded-full">
                        <DollarOutlined className="text-yellow-principal text-3xl" />
                      </div>
                    </div>
                  </OrderSection>

                  {/* Dates Section */}
                  <OrderSection title="Timeline" icon={<CalendarOutlined />}>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <CalendarOutlined className="text-yellow-principal" />
                          <p className="font-android text-zinc-400 text-xs uppercase tracking-wider">
                            Created At
                          </p>
                        </div>
                        <p className="font-barlow text-white text-base">
                          {dayjs(selectedOrder.createdAt).format('MMM DD, YYYY')}
                        </p>
                        <p className="font-barlow text-zinc-500 text-sm">
                          {dayjs(selectedOrder.createdAt).format('HH:mm:ss')}
                        </p>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <ClockCircleOutlined className="text-yellow-principal" />
                          <p className="font-android text-zinc-400 text-xs uppercase tracking-wider">
                            Updated At
                          </p>
                        </div>
                        <p className="font-barlow text-white text-base">
                          {dayjs(selectedOrder.updatedAt).format('MMM DD, YYYY')}
                        </p>
                        <p className="font-barlow text-zinc-500 text-sm">
                          {dayjs(selectedOrder.updatedAt).format('HH:mm:ss')}
                        </p>
                      </div>
                    </div>
                  </OrderSection>

                  {/* Order Items Section */}
                  <OrderSection 
                    title={`Order Items ${selectedOrder.items && selectedOrder.items.length > 0 ? `(${selectedOrder.items.length})` : ''}`} 
                    icon={<ShoppingOutlined />}
                    noWrapper
                  >
                    <div className="bg-zinc-800/30 border border-zinc-700 overflow-hidden">
                      <Table
                        columns={itemColumns}
                        dataSource={selectedOrder.items}
                        rowKey="id"
                        pagination={false}
                        size="middle"
                        className="order-items-table"
                        rowClassName="bg-transparent hover:bg-zinc-800/50 transition-colors"
                        locale={{
                          emptyText: (
                            <div className="py-12">
                              <div className="flex flex-col items-center justify-center">
                                <div className="bg-zinc-800 p-6 mb-4">
                                  <ShoppingOutlined className="text-zinc-600 text-5xl" />
                                </div>
                                <p className="font-android text-zinc-400 text-lg mb-2 uppercase">No items in this order</p>
                                <p className="font-barlow text-zinc-600 text-sm">Order items will appear here</p>
                              </div>
                            </div>
                          )
                        }}
                      />
                    </div>
                  </OrderSection>

                  {/* Footer Actions */}
                  <div className="flex justify-end gap-4 pt-6 border-t border-zinc-700">
                    <Button
                      onClick={() => setDetailModalOpen(false)}
                      type="primary"
                      size="large"
                      className="font-android hover-3d px-8 uppercase tracking-wider h-11"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Modal>
        </div>
      </section>
    </div>
  );
};
